import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { AuthMFAChallenge, AuthMFAEnrollRequired } from '@erebus/http';

/**
 * A Go login that is not finished yet: it needs an MFA code, MFA set up, or a
 * new password (PRX-1745, PRX-2355; cookie mode only).
 *
 * Memory only, never persisted. Both values are short-lived, single-use
 * credentials. After a reload they are gone, and the screens that need them
 * send the user back to log in.
 *
 * Kept out of the auth store on purpose: that store is persisted.
 */
export const useCookieLoginStore = defineStore('cookieLogin', () => {
  /** The challenge from `login()`, for the MFA screen to verify. */
  const pendingMFAChallenge = ref<AuthMFAChallenge | null>(null);

  /**
   * The token from a `must_reset` result: the only credential the user holds
   * until they set a new password. Login and MFA verify each hand out their
   * own, and only the newest one works.
   */
  const pendingResetToken = ref<string | null>(null);

  /**
   * The setup token from an `mfa_enroll_required` result (PRX-2355): the user
   * holds a role that requires MFA and has none yet. Lasts 15 minutes.
   */
  const pendingMFASetup = ref<AuthMFAEnrollRequired | null>(null);

  /** Drops them all, once the login is done or abandoned. */
  function clear(): void {
    pendingMFAChallenge.value = null;
    pendingResetToken.value = null;
    pendingMFASetup.value = null;
  }

  return { pendingMFAChallenge, pendingResetToken, pendingMFASetup, clear };
});
